/**
 * Transforma em link o endereço que a pessoa digitou como texto.
 *
 * Ponto 1 da Gisele (20/10/2026): "links enviados ao cliente devem aparecer
 * como clicáveis".
 *
 * O `<a href>` já funcionava: o `sanitizeHtml` o preserva e ainda põe o
 * `target="_blank"` com o `rel` que fecha o `window.opener`. O que não
 * funcionava era o caso comum -- ninguém digita HTML numa conversa. O operador
 * cola `https://sysfar.com.br/x` na caixa de mensagem, isso chega do outro
 * lado como texto, e o cliente tem de selecionar e copiar à mão.
 *
 * ## Depois da sanitização, sempre
 *
 * Esta função CRIA `<a>`, e o `href` dela não passa pelo DOMPurify se rodar
 * depois dele. Por isso ela não aceita endereço arbitrário: só casa o que
 * começa com `http://`, `https://` ou `www.`, e o `href` é montado aqui a
 * partir do que casou. `javascript:`, `data:` e companhia não têm como entrar
 * por este caminho.
 *
 * Rodar ANTES da sanitização também não serviria: o texto ainda não é HTML,
 * então `<` e `&` do conteúdo se misturariam com as tags que acabamos de
 * escrever.
 *
 * ## Só nós de texto
 *
 * O percurso é pelo DOM, e não por expressão regular sobre a string: sobre a
 * string, o endereço dentro de um `href=` existente seria "encontrado" e
 * viraria um `<a>` dentro de outro `<a>`. Em nó de texto isso não acontece, e
 * quem já está dentro de um link é pulado de propósito.
 *
 * Mesmo nome e mesmo comportamento do arquivo homônimo no clientweb, de
 * propósito: é a mesma decisão, tomada uma vez. O cliente precisa dela para
 * ver o link do operador, e o operador para ver o link do cliente.
 */

/**
 * O que conta como endereço no meio do texto.
 *
 * `www.` sem esquema entra porque é como as pessoas escrevem. Espaço, `<`,
 * `>`, `"` e `'` encerram o endereço -- os quatro últimos porque o conteúdo
 * aqui é HTML e eles delimitam tag e atributo.
 *
 * O caractere logo depois do esquema (ou do `www.`) tem de ser letra ou
 * número: sem essa exigência, "www..." casava, a limpeza da pontuação final o
 * reduzia a "www" e o resultado era um link para `https://www`.
 *
 * Domínio solto, sem `www.` nem esquema, NÃO entra de propósito:
 * "relatorio.xlsx", "versao.2.1" e "sysfar.com.br" têm a mesma forma, e
 * transformar os dois primeiros em link seria pior do que deixar o terceiro
 * como texto.
 */
const ENDERECO = /(?:https?:\/\/|www\.)[a-z0-9][^\s<>"']*/gi;

/**
 * Pontuação que a frase deixou colada no fim do endereço.
 *
 * "Veja em https://sysfar.com.br." tem um ponto que é da frase, não do
 * endereço -- e um link com o ponto a mais abre uma página que não existe.
 */
const PONTUACAO_FINAL = /[.,;:!?'"]+$/;

/**
 * Corta do fim do endereço o que pertence à frase.
 *
 * O fecha-parênteses é o caso chato: em "(https://sysfar.com.br)" ele é da
 * frase, mas em `https://pt.wikipedia.org/wiki/Teste_(x)` é do endereço. A
 * regra é o equilíbrio -- só sobra o fecha que tem abre correspondente.
 */
export const limparFimDoEndereco = (endereco) => {
    let fim = endereco;

    for (;;) {
        const sem = fim.replace(PONTUACAO_FINAL, '');
        if (sem !== fim) { fim = sem; continue; }

        const desbalanceado = /[)\]}]$/.test(fim)
            && contar(fim, /[)\]}]/g) > contar(fim, /[([{]/g);
        if (desbalanceado) { fim = fim.slice(0, -1); continue; }

        return fim;
    }
};

const contar = (texto, padrao) => (texto.match(padrao) || []).length;

/** O `href`: `www.x` sem esquema não navega para fora do site atual. */
const enderecoCompleto = (endereco) => (
    /^www\./i.test(endereco) ? `https://${endereco}` : endereco
);

/**
 * Devolve o mesmo HTML com os endereços em texto virados link.
 *
 * @param {string} html HTML JÁ SANITIZADO
 * @returns {string} HTML com os endereços clicáveis
 */
export const linkificar = (html) => {
    if (!html) return '';

    const container = document.createElement('div');
    container.innerHTML = String(html);

    // A lista é materializada ANTES de mexer no DOM: trocar um nó de texto por
    // vários nós no meio de um percurso vivo faz o percurso visitar os nós que
    // acabamos de criar, e o endereço dentro do `<a>` novo seria linkificado
    // outra vez, para sempre.
    const textos = [];
    const percurso = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    while (percurso.nextNode()) textos.push(percurso.currentNode);

    textos.forEach((no) => {
        if (dentroDeLink(no)) return;
        if (!ENDERECO.test(no.nodeValue || '')) return;
        ENDERECO.lastIndex = 0;

        no.parentNode.replaceChild(comLinks(no.nodeValue), no);
    });

    return container.innerHTML;
};

/**
 * O nó já está dentro de um link?
 *
 * Linkificar aí dentro criaria `<a>` dentro de `<a>`, que o navegador desfaz
 * de um jeito imprevisível -- e o endereço de destino poderia deixar de ser o
 * que o autor escreveu.
 */
const dentroDeLink = (no) => {
    for (let pai = no.parentNode; pai; pai = pai.parentNode) {
        if (pai.nodeName === 'A') return true;
    }

    return false;
};

/** O nó de texto virado fragmento, com `<a>` no lugar de cada endereço. */
const comLinks = (texto) => {
    const fragmento = document.createDocumentFragment();
    let cursor = 0;

    ENDERECO.lastIndex = 0;
    for (let achado = ENDERECO.exec(texto); achado; achado = ENDERECO.exec(texto)) {
        const endereco = limparFimDoEndereco(achado[0]);

        // Sobrou só pontuação (ex.: "www..."): não é endereço, segue como texto.
        if (!/[a-z0-9]$/i.test(endereco) && !/\/$/.test(endereco)) continue;

        const inicio = achado.index;
        if (inicio > cursor) {
            fragmento.appendChild(document.createTextNode(texto.slice(cursor, inicio)));
        }

        const link = document.createElement('a');
        link.setAttribute('href', enderecoCompleto(endereco));
        // Os mesmos atributos que o `sanitizeHtml` põe nos links que já vêm
        // como HTML: nova aba para não perder a conversa, e `rel` fechando o
        // acesso ao `window.opener` da página aberta.
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
        link.textContent = endereco;
        fragmento.appendChild(link);

        cursor = inicio + endereco.length;
        // A pontuação cortada do fim volta como texto, e o `lastIndex` recua
        // junto: sem isto, "veja https://a.com, e https://b.com" perdia a
        // vírgula e o segundo endereço.
        ENDERECO.lastIndex = cursor;
    }

    if (cursor < texto.length) {
        fragmento.appendChild(document.createTextNode(texto.slice(cursor)));
    }

    return fragmento;
};

export default linkificar;
